import React, { useEffect, useRef } from 'react';
import {
  Form,
  FormInstance,
  Checkbox,
  FormErrors
} from '@lightbeamhealth/blueprint-form';
import { produce } from 'immer';
import { Container, Row, Col } from '../../../grid';
import Input from '../../molecules/input';
import Button from '../../molecules/button';
import Card from '../card';
import ErrorCallout from '../callout/error-callout';
import Modal from '../modal';
import Tooltip from '../tooltip';
import { useMetaData } from '../../../contexts/metadata';
import Filter from './filter';
import AppsFilter from './apps/filter';
import {
  getFilter,
  headerText,
  filterCardStyles,
  PatientGroupFormProps,
  PatientGroupFormData
} from './utils';
import { useEditPatientGroup } from '../../../libs/group';
import { useAuth } from '../../../contexts/auth';

/**
 * Render error callout when have an edit error
 *
 * @param error string or error object
 */
const renderError = (error: any): JSX.Element => {
  if (!error) {
    return null;
  }
  let message = '';
  if (typeof error === 'string') {
    message = error;
  }
  if (error.message) {
    message = error.message;
  }
  return <ErrorCallout className="mt-4 mb-6">{message}</ErrorCallout>;
};

interface DeleteButtonProps {
  numGroups: number;
  isEnterpriseAccount: boolean;
  isDefaultGroup: boolean;
  removeGroup: () => void;
}

const DeleteButton = ({
  numGroups,
  isEnterpriseAccount,
  isDefaultGroup,
  removeGroup
}: DeleteButtonProps): JSX.Element => {
  let isDisabled = false;
  if (isDefaultGroup || (numGroups === 1 && isEnterpriseAccount)) {
    isDisabled = true;
  }
  let content = 'It is not possible to delete this view!';
  if (isDefaultGroup) {
    content = 'It is not possible to delete a default view!';
  } else if (numGroups === 1 && isEnterpriseAccount) {
    content =
      'It is not possible to delete this view because this is an enterprise account and you only have one view!';
  }
  return (
    <Tooltip content={content} position="right" disabled={!isDisabled}>
      <Button fill intent="danger" onClick={removeGroup} disabled={isDisabled}>
        Delete
      </Button>
    </Tooltip>
  );
};

/**
 * Form used to edit active patient group or create a new patient group
 *
 * @param isNew boolean, used to determine if creating a new patient group
 * or editing the active patient group
 */
const PatientGroupForm = ({
  isNew,
  toggleForm
}: PatientGroupFormProps): JSX.Element => {
  const {
    pgData,
    getActiveGroup,
    pgLoading,
    createPatientGroup,
    editPatientGroup,
    editSuccess,
    editError,
    resetRequests,
    removePatientGroup,
    removeSuccess,
    removeError
  } = useEditPatientGroup();
  const headerRef = useRef<HTMLHeadingElement>(null);
  const { terms } = useMetaData();
  const { user } = useAuth();
  useEffect(() => {
    headerRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  const activeGroup = getActiveGroup();
  const numGroups = pgData?.groups?.length || 0;
  const isEnterpriseAccount = user?.account?.isEnterprise || false;
  const groupId = activeGroup ? activeGroup.id : null;
  const error = editError || removeError;
  const initialData: PatientGroupFormData = {
    name: '',
    isDefault: false,
    description: '',
    filters: {
      contactIds: getFilter(activeGroup, 'contactIds'),
      apps: getFilter(activeGroup, 'apps'),
      tagIds: getFilter(activeGroup, 'tagIds')
    }
  };

  const form = new FormInstance<PatientGroupFormData>(initialData);

  const validate = (values: PatientGroupFormData): FormErrors => {
    const errors: FormErrors = {};
    for (const filterKey in values.filters) {
      const filterValue = values.filters[filterKey];
      if (filterValue && filterValue.length) {
        delete errors.filters;
        break;
      }
      errors.filters = 'At least one filter must be selected!';
    }
    return errors;
  };

  // Remove filters that are empty b/c we don't want to save those to the DB
  // TO DO: Make this less reliant on specific key references
  const serialize = (data: PatientGroupFormData): PatientGroupFormData => {
    const params = produce(data, (draft) => {
      if (!draft.filters.apps.length) {
        delete draft.filters.apps;
      }
      if (!draft.filters.contactIds.length) {
        delete draft.filters.contactIds;
      }
      if (!draft.filters.tagIds.length) {
        delete draft.filters.tagIds;
      }
    });
    return params;
  };

  // Edit or create a new group
  const saveGroup = (data: PatientGroupFormData) => {
    const params = serialize(data);
    if (isNew) {
      createPatientGroup(params);
    } else {
      editPatientGroup({
        id: groupId,
        ...params
      });
    }
  };

  // Remove a group
  const removeGroup = () => {
    Modal.confirm({
      title: `Delete ${terms.patient.capitalized} View`,
      content: `Are you sure you want to delete this ${terms.patient.capitalized} View? This action is not reversible, so you will have to make this View again from scratch once deleted.`,
      onOk: () => {
        removePatientGroup({ id: activeGroup.id });
      }
    });
  };

  // If we have a group id, then we're editing
  // so we need to set the form to that group's values
  useEffect(() => {
    if (isNew) {
      form.setData(initialData);
    } else {
      form.setData({
        name: activeGroup ? activeGroup.name : '',
        isDefault: activeGroup ? activeGroup.isDefault : false,
        description: activeGroup ? activeGroup.description : '',
        filters: {
          contactIds: getFilter(activeGroup, 'contactIds'),
          apps: getFilter(activeGroup, 'apps'),
          tagIds: getFilter(activeGroup, 'tagIds')
        }
      });
    }
  }, [groupId, isNew]);

  useEffect(() => {
    if (removeSuccess) {
      toggleForm('new', true);
      form.getForm().resetForm();
      resetRequests();
    }
  }, [removeSuccess]);

  // On success, reset new forms to be blank
  useEffect(() => {
    if (editSuccess) {
      toggleForm('new', true);
      form.getForm().resetForm();
      resetRequests();
    }
  }, [editSuccess, isNew]);

  return (
    <Container>
      <Row>
        <Col md={18} offset={{ md: 3 }}>
          <div className="mt-6 mb-6">
            <Container fluid>
              <h2
                ref={headerRef}
                className="text-serif-xxl font-serif font-normal"
              >
                {headerText(isNew, activeGroup)}
              </h2>
              <Form form={form} validate={validate} onSubmit={saveGroup}>
                {(props) => (
                  <React.Fragment>
                    {renderError(error)}
                    <Row>
                      <Col md={12}>
                        <Input label="Name" name="name" required />
                      </Col>
                      <Col md={12}>
                        <div style={{ marginTop: '27px' }}>
                          <Checkbox
                            label="Use as default view"
                            name="isDefault"
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Input label="Description" name="description" />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <h4 className="my-0">Filters</h4>
                        <p>
                          You must select at least one of the following filters.
                        </p>
                        <Card className={filterCardStyles(props.errors)}>
                          <Container fluid>
                            {props.errors && props.errors.filters && (
                              <ErrorCallout className="mt-4 mb-6">
                                {props.errors.filters}
                              </ErrorCallout>
                            )}
                            <Row>
                              <Col md={8}>
                                <Filter
                                  filterType="contacts"
                                  groupLoading={pgLoading}
                                  selectedIds={props.values.filters.contactIds}
                                  update={props.setFieldValue}
                                />
                              </Col>
                              <Col md={8}>
                                <AppsFilter
                                  selectedAppKeys={props.values.filters.apps}
                                  update={props.setFieldValue}
                                />
                              </Col>
                              <Col md={8}>
                                <Filter
                                  filterType="tags"
                                  groupLoading={pgLoading}
                                  selectedIds={props.values.filters.tagIds}
                                  update={props.setFieldValue}
                                />
                              </Col>
                            </Row>
                          </Container>
                        </Card>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3}>
                        <Button
                          variant="minimal"
                          fill
                          onClick={() =>
                            toggleForm(isNew ? 'new' : 'edit', true)
                          }
                        >
                          Close
                        </Button>
                      </Col>
                      <Col md={3}>
                        <Button fill intent="primary" type="submit">
                          Save
                        </Button>
                      </Col>
                      {!isNew && (
                        <Col md={3}>
                          <DeleteButton
                            isDefaultGroup={props.values.isDefault}
                            numGroups={numGroups}
                            isEnterpriseAccount={isEnterpriseAccount}
                            removeGroup={removeGroup}
                          />
                        </Col>
                      )}
                    </Row>
                  </React.Fragment>
                )}
              </Form>
            </Container>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PatientGroupForm;
